/**
 * 我的充值页面 - 运营商充值管理
 */

import { useState, useEffect } from 'react'
import { useAuthStore } from '@/store/authStore'
import { supabase } from '@/lib/supabase'
import { PlusIcon, PhotoIcon } from '@heroicons/react/24/outline'
import okxService, { OKXPaymentOrder } from '@/services/okxService'
import OKXPaymentModal from '@/components/OKXPaymentModal'

interface Deposit {
  id: string
  okx_order_id?: string
  amount: number
  fee: number
  real_amount: number
  status: number
  payment_method: string
  payment_proof?: string
  created_at: string
  processed_at?: string
}

interface PaymentChannel {
  channel_code: string
  channel_name: string
  channel_icon: string
}

export default function MyDepositsPage() {
  const { user } = useAuthStore()
  const [deposits, setDeposits] = useState<Deposit[]>([])
  const [loading, setLoading] = useState(true)
  const [showApplyModal, setShowApplyModal] = useState(false)
  const [paymentChannels, setPaymentChannels] = useState<PaymentChannel[]>([])
  const [loadingChannels, setLoadingChannels] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  
  // OKX 支付相关状态
  const [showOKXModal, setShowOKXModal] = useState(false)
  const [okxPaymentOrder, setOKXPaymentOrder] = useState<OKXPaymentOrder | null>(null)
  
  // 表单状态
  const [formData, setFormData] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    payment_proof: ''
  })

  // 运营商账户信息
  const [operatorAccount, setOperatorAccount] = useState({
    balance: 0,
    commission_balance: 0
  })

  useEffect(() => {
    fetchDeposits()
    fetchOperatorAccount()
    fetchPaymentChannels()
  }, [])

  const fetchPaymentChannels = async () => {
    setLoadingChannels(true)
    try {
      const { data, error } = await supabase
        .from('payment_channels')
        .select('channel_code, channel_name, channel_icon')
        .eq('is_enabled', true)
        .order('channel_code')

      if (error) throw error
      setPaymentChannels(data || [])
      
      // 设置默认支付方式为第一个可用通道
      if (data && data.length > 0) {
        setFormData(prev => ({ ...prev, payment_method: data[0].channel_code }))
      }
    } catch (error) {
      console.error('Failed to fetch payment channels:', error)
      setPaymentChannels([])
    } finally {
      setLoadingChannels(false)
    }
  }

  const fetchDeposits = async () => {
    try {
      setLoading(true)
      
      // 获取 operator_id
      const { data: adminUser, error: adminError } = await supabase
        .from('admin_users')
        .select('operator_id')
        .eq('id', user?.id)
        .maybeSingle()

      if (adminError || !adminUser?.operator_id) {
        console.error('Failed to get operator_id:', adminError)
        setDeposits([])
        return
      }

      const { data, error } = await supabase
        .from('deposit_orders')
        .select(`
          id, 
          amount, 
          fee, 
          real_amount, 
          status, 
          payment_method, 
          payment_proof, 
          created_at, 
          processed_at,
          okx_payment_orders(okx_order_id)
        `)
        .eq('operator_id', adminUser.operator_id)
        .eq('order_type', 2)
        .order('created_at', { ascending: false })

      if (error) throw error

      const formattedData = (data || []).map((item: any) => ({
        id: item.id,
        okx_order_id: item.okx_payment_orders?.[0]?.okx_order_id || item.id,
        amount: parseFloat(item.amount),
        fee: parseFloat(item.fee || '0'),
        real_amount: parseFloat(item.real_amount),
        status: item.status,
        payment_method: item.payment_method,
        payment_proof: item.payment_proof,
        created_at: item.created_at,
        processed_at: item.processed_at
      }))

      setDeposits(formattedData)
    } catch (error) {
      console.error('Failed to fetch deposits:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchOperatorAccount = async () => {
    try {
      // 第一步：从 admin_users 获取 operator_id
      const { data: adminUser, error: adminError } = await supabase
        .from('admin_users')
        .select('operator_id, role')
        .eq('id', user?.id)
        .maybeSingle()

      if (adminError) {
        console.error('Failed to fetch admin user:', adminError)
        return
      }

      if (!adminUser || adminUser.role !== 'operator' || !adminUser.operator_id) {
        console.warn('User is not an operator or has no operator_id')
        setOperatorAccount({
          balance: 0,
          commission_balance: 0
        })
        return
      }

      // 第二步：使用 operator_id 查询 operator_accounts
      const { data, error } = await supabase
        .from('operator_accounts')
        .select('balance, commission_balance')
        .eq('operator_id', adminUser.operator_id)
        .maybeSingle()

      if (error) {
        console.error('Failed to fetch operator account:', error)
        return
      }
      
      if (data) {
        setOperatorAccount({
          balance: parseFloat(data.balance || '0'),
          commission_balance: parseFloat(data.commission_balance || '0')
        })
      } else {
        console.warn('No operator account found for operator_id:', adminUser.operator_id)
        setOperatorAccount({
          balance: 0,
          commission_balance: 0
        })
      }
    } catch (error) {
      console.error('Failed to fetch operator account:', error)
      setOperatorAccount({
        balance: 0,
        commission_balance: 0
      })
    }
  }

  const handleApplyDeposit = async () => {
    try {
      const amount = parseFloat(formData.amount)
      
      if (!amount || amount <= 0) {
        alert('请输入有效的充值金额')
        return
      }

      if (amount < 100) {
        alert('最低充值金额为 $100')
        return
      }

      // 获取 operator_id
      const { data: adminUser, error: adminError } = await supabase
        .from('admin_users')
        .select('operator_id')
        .eq('id', user?.id)
        .single()

      if (adminError || !adminUser?.operator_id) {
        throw new Error('无法获取运营商信息')
      }

      // 如果选择 OKX 支付，使用实时支付流程
      if (formData.payment_method === 'okx') {
        const result = await okxService.createOKXPayment({
          amount: amount,
          currency: 'USDT',
          chain: 'TRC20'
        })

        if (!result.success || !result.data) {
          throw new Error(result.error || '创建OKX支付订单失败')
        }

        // 显示 OKX 支付模态框
        setOKXPaymentOrder(result.data)
        setShowOKXModal(true)
        setShowApplyModal(false)
        setFormData({ amount: '', payment_method: 'okx', payment_proof: '' })
        return
      }

      // 其他支付方式：调用 RPC 函数创建运营商充值订单
      const { data, error } = await supabase.rpc('rpc_operator_deposit', {
        p_operator_id: adminUser.operator_id,
        p_amount: amount,
        p_payment_method: formData.payment_method,
        p_payment_proof: 'pending' // 使用默认值，不需要用户上传
      })

      if (error) throw error

      alert('充值申请已提交，等待管理员审核')
      setShowApplyModal(false)
      setFormData({ amount: '', payment_method: 'bank_transfer', payment_proof: '' })
      fetchDeposits()
    } catch (error) {
      console.error('Failed to apply deposit:', error)
      alert('充值申请失败：' + (error as Error).message)
    }
  }

  const getStatusBadge = (status: number) => {
    const statusConfig: Record<number, { label: string; className: string }> = {
      0: { label: '待支付', className: 'bg-gray-100 text-gray-800' },
      1: { label: '支付中', className: 'bg-yellow-100 text-yellow-800' },
      2: { label: '支付成功', className: 'bg-green-100 text-green-800' },
      3: { label: '支付失败', className: 'bg-red-100 text-red-800' },
      4: { label: '已退款', className: 'bg-blue-100 text-blue-800' }
    }
    const config = statusConfig[status] || { label: '未知', className: 'bg-gray-100 text-gray-800' }
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    )
  }

  const getPaymentMethodLabel = (method: string) => {
    const channel = paymentChannels.find(ch => ch.channel_code === method)
    return channel ? channel.channel_name : method
  }

  const getPaymentMethodIcon = (method: string) => {
    const channel = paymentChannels.find(ch => ch.channel_code === method)
    return channel ? channel.channel_icon : '💳'
  }

  const handleCopyOrderId = async (orderId: string) => {
    try {
      await navigator.clipboard.writeText(orderId)
      setCopiedId(orderId)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  // 快捷金额
  const quickAmounts = [100, 500, 1000, 5000, 10000]

  const handleQuickAmount = (amount: number) => {
    setFormData(prev => ({ ...prev, amount: amount.toString() }))
  }

  return (
    <div className="page-content">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">我的充值</h1>
            <p className="page-subtitle">管理您的账户充值</p>
          </div>
          <button
            onClick={() => setShowApplyModal(true)}
            className="btn btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>申请充值</span>
          </button>
        </div>
      </div>

      {/* 账户余额卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="card">
          <div className="card-body">
            <h3 className="text-sm font-medium text-gray-500 mb-2">账户余额</h3>
            <p className="text-3xl font-bold text-blue-600">
              ¥{operatorAccount.balance.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-gray-500 mt-2">用于创建用户和充值赠金</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <h3 className="text-sm font-medium text-gray-500 mb-2">佣金余额</h3>
            <p className="text-3xl font-bold text-green-600">
              ¥{operatorAccount.commission_balance.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-gray-500 mt-2">来自用户交易手续费分成</p>
          </div>
        </div>
      </div>

      {/* 充值记录 */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">充值记录</h3>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-12">
              <div className="text-gray-500">加载中...</div>
            </div>
          ) : deposits.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500 mb-4">还没有充值记录</div>
              <button
                onClick={() => setShowApplyModal(true)}
                className="btn btn-primary"
              >
                申请第一笔充值
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      订单ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      充值金额
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      支付方式
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      支付凭证
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状态
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      申请时间
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      完成时间
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {deposits.map((deposit) => (
                    <tr key={deposit.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-mono text-gray-600">
                            {deposit.okx_order_id ? 
                              (deposit.okx_order_id.length > 20 ? 
                                deposit.okx_order_id.substring(0, 20) + '...' : 
                                deposit.okx_order_id
                              ) : 
                              deposit.id.substring(0, 8) + '...'
                            }
                          </span>
                          <button
                            onClick={() => handleCopyOrderId(deposit.okx_order_id || deposit.id)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            title="复制完整订单ID"
                          >
                            {copiedId === (deposit.okx_order_id || deposit.id) ? (
                              <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          ¥{deposit.amount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className="text-xl">{getPaymentMethodIcon(deposit.payment_method)}</span>
                          <span className="text-sm text-gray-900">{getPaymentMethodLabel(deposit.payment_method)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {deposit.payment_proof ? (
                          <a
                            href={deposit.payment_proof}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                          >
                            <PhotoIcon className="w-4 h-4" />
                            <span>查看</span>
                          </a>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(deposit.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(deposit.created_at).toLocaleString('zh-CN')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {deposit.processed_at ? new Date(deposit.processed_at).toLocaleString('zh-CN') : '-'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 申请充值模态框 */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-6 border w-full max-w-md shadow-lg rounded-lg bg-white">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">申请充值</h3>

              <div className="space-y-5">
                {/* 当前余额提示 */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-sm text-blue-800">
                    当前余额: <span className="font-bold text-lg">¥{operatorAccount.balance.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="text-xs text-blue-600 mt-1">
                    充值后可用于创建用户和充值赠金
                  </div>
                </div>

                {/* 充值金额输入 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">充值金额 *</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                    placeholder="请输入充值金额"
                    min="100"
                    step="0.01"
                  />
                  <p className="mt-2 text-xs text-gray-500">最低充值金额: $100，免手续费</p>
                </div>

                {/* 快捷金额 */}
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">快捷金额</div>
                  <div className="grid grid-cols-3 gap-2">
                    {quickAmounts.map((amount) => (
                      <button
                        key={amount}
                        type="button"
                        onClick={() => handleQuickAmount(amount)}
                        className="py-3 px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                      >
                        ${amount}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 支付方式选择 */}
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">支付方式 *</div>
                  {loadingChannels ? (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      加载支付方式中...
                    </div>
                  ) : paymentChannels.length === 0 ? (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      暂无可用支付方式
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {paymentChannels.map((channel) => (
                        <button
                          key={channel.channel_code}
                          type="button"
                          onClick={() => setFormData({ ...formData, payment_method: channel.channel_code })}
                          className={`py-2 px-2 rounded-lg border-2 transition-all ${
                            formData.payment_method === channel.channel_code
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <div className="text-xl mb-1">{channel.channel_icon}</div>
                          <div className="text-xs font-medium">{channel.channel_name}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* 金额预览 */}
                {formData.amount && parseFloat(formData.amount) >= 100 && (
                  <div className="p-4 bg-gray-50 rounded-lg space-y-3 text-sm border border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">充值金额:</span>
                      <span className="font-semibold text-gray-900 text-lg">¥{parseFloat(formData.amount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">手续费:</span>
                      <span className="text-green-600 font-medium">¥0.00 (免费)</span>
                    </div>
                    <div className="h-px bg-gray-200"></div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-900 font-medium">到账金额:</span>
                      <span className="text-green-600 font-bold text-xl">¥{parseFloat(formData.amount).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* 按钮 */}
              <div className="flex justify-end space-x-3 mt-8">
                <button
                  onClick={() => {
                    setShowApplyModal(false)
                    setFormData({ amount: '', payment_method: paymentChannels[0]?.channel_code || 'bank_transfer', payment_proof: '' })
                  }}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleApplyDeposit}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!formData.amount || parseFloat(formData.amount) < 100}
                >
                  提交申请
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OKX 支付模态框 */}
      <OKXPaymentModal
        isOpen={showOKXModal}
        onClose={() => setShowOKXModal(false)}
        paymentOrder={okxPaymentOrder}
        onPaymentSuccess={async () => {
          // 支付成功后刷新充值记录和余额
          await fetchDeposits()
          await fetchOperatorAccount()
        }}
      />
    </div>
  )
}
